/**
 * Boverkets byggregler (BBR) – kapitelvisa förklaringar.
 * Innehåller inte fulltextregler – bara sammanfattningar och exempel.
 */
export const bbrRules = [
  {
    slug: "kap-3-hygien-halsa-miljo",
    title: "BBR kap 3 – Hygien, hälsa och miljö",
    h1: "BBR kap 3 – Hygien, hälsa och miljö",
    metaDescription:
      "BBR kapitel 3 om ventilation, dagsljus, radon och fukt. Krav på bostäder och komplementbyggnader – exempel för villa och attefallshus.",
    keywords: "BBR kap 3, ventilation krav, dagsljus, radon, fukt",
    intro:
      "Kapitel 3 säkerställer att byggnaden erbjuder en hälsosam inomhusmiljö – dagsljus, ventilation, fukt- och radonskydd samt vatten och avlopp.",
    excerpt: {
      text: "Byggnader ska utformas och deras installationer väljas och utföras så att luft- och vattenkvalitet samt ljus-, fukt-, temperatur- och hygienförhållanden blir tillfredsställande.",
      source: "BBR 3:1 – Allmänt",
    },
    examples: [
      { type: "Villa", title: "Ventilation", text: "Minsta luftflöde i bostad: 0,35 l/s per m² golv – kan uppnås med FTX eller självdrag med värmeväxlare." },
      { type: "Attefall", title: "Dagsljus", text: "Även 30 m² attefallshus omfattas av dagsljuskraven om det används som bostad." },
    ],
    mistakes: [
      "Att bortse från radonkrav vid nybyggnad – markradon ska mätas eller åtgärdas i utsatta områden.",
      "Att glömma fuktsäkring under sylltrösklar – vanligt fel i självbyggda attefall.",
    ],
    municipality_variations:
      "BBR är nationell – kommuner kan inte skärpa eller mildra kraven i bygglovsbedömningen.",
    faq: [
      { q: "Måste ett attefallshus ha ventilation?", a: "Ja, om det används för bostadsändamål gäller samma BBR-krav som villa." },
      { q: "Vad är radonriktvärdet?", a: "Max 200 Bq/m³ i inomhusluft enligt BBR 6:23." },
    ],
    related: ["bbr/kap-5-brandskydd", "bbr/kap-9-energihushallning"],
    updated: "2026-07-04",
  },
  {
    slug: "kap-5-brandskydd",
    title: "BBR kap 5 – Brandskydd",
    h1: "BBR kap 5 – Brandskydd i byggnader",
    metaDescription:
      "BBR 5 om brandceller, brandvarnare, utrymningsvägar och avstånd till tomtgräns. Krav för villa, attefallshus och garage.",
    keywords: "BBR kap 5, brandskydd, brandvarnare, avstånd tomtgräns",
    intro:
      "Kapitel 5 ställer krav på brandskydd – från brandvarnare i sovrum till avstånd mellan byggnader. Reglerna gäller alla byggnader, även attefall.",
    excerpt: {
      text: "Byggnader ska utformas med sådant brandskydd att brandsäkerheten blir tillfredsställande.",
      source: "BBR 5:1 – Allmänt",
    },
    examples: [
      { type: "Villa", title: "Brandvarnare", text: "Minst en brandvarnare per våning + i eller vid varje sovrum (BBR 5:2513)." },
      { type: "Attefall", title: "Avstånd till huvudbyggnad", text: "Attefallshus närmare än 4 m till huvudbyggnad kräver särskilt brandskydd (t.ex. obrännbar fasad)." },
      { type: "Garage", title: "Villa + garage", text: "Sammanbyggt garage måste ha branddörr och EI-klassad vägg (typ EI30) mot bostad." },
    ],
    mistakes: [
      "Att glömma brandsäkert avstånd till granne – normalt minst 4 m till annan byggnad.",
      "Att sätta attefallshus tätt intill villa utan brandskyddad fasad – kan innebära hela huset måste brandsäkras.",
      "Att bygga med tändande material (t.ex. papptak) nära grill/eldstad.",
    ],
    municipality_variations:
      "BBR är nationell men i tät bebyggelse kan detaljplan kräva ytterligare brandkrav (t.ex. i innerstad).",
    faq: [
      { q: "Hur nära tomtgräns får jag bygga?", a: "Enligt PBL 9 kap. 5 § minst 4,5 m om inte grannen medger annat. BBR-brandkrav kan skärpa detta ytterligare." },
      { q: "Krävs brandvarnare i garage?", a: "Ja om det är sammanbyggt med bostad. Fristående garage har lägre krav." },
      { q: "Vad är EI30, EI60?", a: "Klasser för brandmotstånd – EI30 = tätt/isolerande i 30 minuter. EI60 för sammanbyggda garage rekommenderas." },
    ],
    howto: {
      name: "Så uppfyller du brandkrav i BBR",
      steps: [
        { name: "1. Identifiera byggnadstyp", text: "Bostad, komplementbyggnad, verksamhet – olika krav gäller." },
        { name: "2. Räkna avstånd", text: "Minst 4 m till annan byggnad utan brandskydd, annars 4,5 m till tomtgräns." },
        { name: "3. Installera brandvarnare", text: "Minst en per våning + i sovrum. Anslut till 230 V eller batteri (10-årsbatteri)." },
        { name: "4. Utrymningsväg", text: "Varje sovrum ska ha fönster som kan öppnas för utrymning (>0,4 m höjd, >0,5 m bredd)." },
      ],
    },
    related: ["bbr/kap-8-tillganglighet", "bygglov/garage", "attefall/attefallshus-regler"],
    updated: "2026-07-04",
  },
  {
    slug: "kap-8-tillganglighet",
    title: "BBR kap 8 – Tillgänglighet",
    h1: "BBR kap 8 – Tillgänglighet och användbarhet",
    metaDescription:
      "BBR 8 om tillgänglighet: dörrbredder, ramper, hiss och rullstolsvänliga badrum. Krav på bostäder, allmänna byggnader och attefall.",
    keywords: "BBR kap 8, tillgänglighet, rullstol, dörrbredd, hiss",
    intro:
      "Kapitel 8 säkerställer att byggnader kan användas av personer med nedsatt rörelse- eller orienteringsförmåga.",
    excerpt: {
      text: "Byggnader ska vara tillgängliga och användbara för personer med nedsatt rörelse- eller orienteringsförmåga.",
      source: "BBR 3:1 (allmän utgångspunkt) och 8:1 detaljreglering",
    },
    examples: [
      { type: "Villa", title: "Ingång", text: "Nybyggd villa ska ha minst en tillgänglig entré – helst utan trappsteg eller med ramp." },
      { type: "Attefall", title: "Bostadsattefall", text: "Attefallshus för permanentboende omfattas av tillgänglighetskraven." },
    ],
    mistakes: [
      "Att glömma hiss i flerbostadshus över 2 våningar.",
      "Att göra badrummet för smalt – rullstolsvändcirkel kräver 1,3 m fritt utrymme.",
    ],
    municipality_variations:
      "BBR är nationell. Vissa kommuner ställer mildare tillgänglighetskrav på fritidshus i vissa detaljplaner.",
    faq: [
      { q: "Vilken minsta dörrbredd?", a: "Fri passagebredd 0,80 m för invändiga dörrar, 0,90 m för entrédörr." },
      { q: "Krävs hiss?", a: "Vid fler än 2 våningar i flerbostadshus. Villa/attefall omfattas inte." },
    ],
    related: ["bbr/kap-3-hygien-halsa-miljo", "bbr/kap-9-energihushallning"],
    updated: "2026-07-04",
  },
  {
    slug: "kap-9-energihushallning",
    title: "BBR kap 9 – Energihushållning",
    h1: "BBR kap 9 – Energihushållning och U-värden",
    metaDescription:
      "BBR 9 om primärenergital, U-värden och krav på klimatskal. Så beräknas energikraven för villa, attefall och tillbyggnad.",
    keywords: "BBR kap 9, energihushållning, primärenergital, U-värde, klimatskal",
    intro:
      "Kapitel 9 sätter maxvärden för byggnadens energianvändning uttryckt som primärenergital (kWh/m² Atemp och år).",
    excerpt: {
      text: "Byggnader ska vara utformade så att energianvändningen begränsas genom låga värmeförluster, lågt kylbehov, effektiv värme- och kylanvändning och effektiv elanvändning.",
      source: "BBR 9:1 – Allmänt",
    },
    examples: [
      { type: "Villa", title: "Nybyggd villa", text: "Primärenergital max 75 kWh/m²·år (klimatzon III, elvärmd)." },
      { type: "Attefall", title: "Attefallshus", text: "Uppfyller lättare energikrav om golvarea ≤ 50 m² och används som gäststuga (icke-permanent boende)." },
    ],
    mistakes: [
      "Att glömma köldbryggor vid takfot – kan öka U-värdet med 20 %.",
      "Att sätta för klen värmepump – primärenergital blir för högt vid dimensioneringsfel.",
    ],
    municipality_variations:
      "BBR är nationell. Vissa kommuner uppmuntrar plusenergi via bonusar men får inte skärpa nationella krav i bygglovet.",
    faq: [
      { q: "Vad är primärenergital?", a: "Ett viktat mått på köpt energi. Beräknas som elenergi × 1,8, fjärrvärme × 0,7 osv. Divideras med Atemp." },
      { q: "Måste attefall uppfylla BBR 9?", a: "Ja om det används permanent. Nej om det är rent förråd eller carport." },
    ],
    related: ["bbr/kap-3-hygien-halsa-miljo", "bygglov/solceller"],
    updated: "2026-07-04",
  },
];
